/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Drawer, IModuleProps, INodeProps, Module } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ProductSpecificationTableRow } from './components/dlvry-product-specification-table-stucture';
import { IDlvryProductSpecificationData } from './dlvry-product-specification.data';
import { displayStyle, IDlvryProductSpecificationProps } from './dlvry-product-specification.props.autogenerated';
import { getAttributeStringOrDefault, getAttributeText, getAttributesLocale, parseAttributesMap } from '../../shared-utils/attributes';
import { AttributesForSelectedVariantInput, getAttributesForSelectedVariantAction } from '../../actions/get-attributes-for-selected-variant';
import { ProductSpecificationTableAllergenRow } from './components/dlvry-product-specification-table-structure-allergen';
import { sortMediaLocations } from './common/media-location-sorter';
import { getFlagIconUrl } from '../../shared-utils/flags';

/**
 * Maintain Drawer State.
 */
export interface IProductSpecificationState {
    isDrawerOpen: boolean;
    drawerListId: number | undefined;
}

/**
 * Product specification view props.
 */
export interface IProductSpecificationViewProps extends IDlvryProductSpecificationProps<IDlvryProductSpecificationData> {
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: (JSX.Element | null)[];
    title?: React.ReactNode;
    isAccrodion?: boolean;
    handleText?(event: Msdyn365.ContentEditableEvent): void;
    addionalMedia: React.ReactNode;
    curatedInformation: React.ReactNode;
    ingredients: React.ReactNode;
    footer: React.ReactNode;
}

/**
 * Check for display style either accordion or table.
 */
export interface IProductSpecificationExtentedProps extends IDlvryProductSpecificationProps<IDlvryProductSpecificationData> {
    displayStyle?: displayStyle;
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<IDlvryProductSpecificationProps<IDlvryProductSpecificationData>>}
 */
@observer
class ProductSpecification extends React.Component<IProductSpecificationExtentedProps, IProductSpecificationState> {

    private readonly _attributesLocale = getAttributesLocale(this.props.context.request.locale);

    public constructor(props: IProductSpecificationExtentedProps) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            drawerListId: undefined
        };
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);

        console.log('attrs', JSON.parse(JSON.stringify(this.props.data.productSpecificationData.result || {})));

        reaction(
            () => this.props.data.productSpecificationData.result,
            () => {
                console.log('attrs', JSON.parse(JSON.stringify(this.props.data.productSpecificationData.result)));
            }
        );

        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        context.request.locale,
                        data.product.result.RecordId,
                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariantAction(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        let data: IDlvryProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { context } = this.props;
        const { heading, className } = this.props.config;
        const { productSpecificationData } = this.props.data;
        const isAccrodion = this.props.config.displayStyle === displayStyle.accordion;
        const ingredients = getAttributeStringOrDefault(context.request.locale, productSpecificationData.result, 'ingredients', undefined);

        const viewProps = {
            ...this.props,
            handleText: this.handleHeadingChange,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: isAccrodion
                ? {
                      moduleProps: this.props,
                      className: classnames('ms-product-specification__accordion', className)
                  }
                : {
                      className: classnames('ms-product-specification__table table-striped table-bordered'),
                      tag: 'table'
                  },
            isAccrodion,
            ProductSpecificationTableBodyProps: isAccrodion
                ? {}
                : {
                      className: '',
                      tag: 'tbody'
                  },
            title: heading?.text && (
                <Msdyn365.Text
                    className='ms-product-specification__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),

            productSpecificationResult: isAccrodion
                ? productSpecificationData.result && this._createDrawerBody(data)
                : productSpecificationData.result && this._createTableBody(data),
            addionalMedia: this._renderAddionalMedia(),
            curatedInformation: this._renderCuratedInformation(),
            ingredients: ingredients
                ?   <div className='ms-product-specification__ingredients'>
                        <h2>{this._attributesLocale.labels.ingredients}</h2>
                        <div>
                            {ingredients}
                        </div>
                    </div>
                : null,
            footer: null
        } as IProductSpecificationViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _createTableBody(content: IDlvryProductSpecificationData): { heading: any; tableData: any }[] {
        const productSpecificationData = content.productSpecificationData.result;
        const countryAttributeText = getAttributeText(this.props.context.request.locale, 'countryOfOrigin');
        const attributesMap = parseAttributesMap(this.props.context, productSpecificationData);
        const tables: {
            name: string;
            heading: JSX.Element;
            subHeader: JSX.Element | null;
            tableData: (JSX.Element | null)[];
            modelVersion?: string;
        }[] = [];

        attributesMap.forEach((group) => {
            const table = group.attributes.map((attribute: { name: string; value: string }, index: number) => {
                if (group.type === 'allergens') {
                    return (
                        <ProductSpecificationTableAllergenRow
                            className='ms-product-specification__table-row-allergen'
                            key={index}
                            locale={this.props.context.request.locale}
                            productName={attribute.name}
                            value={attribute.value}
                        />
                    );
                }

                let flagIconUrl: string | undefined;

                if (attribute.name === countryAttributeText) {
                    flagIconUrl = getFlagIconUrl(attribute.value);
                }

                return (
                    <ProductSpecificationTableRow
                        className='ms-product-specification__table-row'
                        key={index}
                        productName={attribute.name}
                        cellData={(
                            flagIconUrl ? (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <img className='ms-product-specification__table-row-flag' src={flagIconUrl} alt={`${attribute.name}: ${attribute.value}`} />
                                    <div dangerouslySetInnerHTML={{ __html: attribute.value }}></div>
                                </div>
                            ) : (
                                <div dangerouslySetInnerHTML={{ __html: attribute.value }}></div>
                            )
                        )}
                    />
                );
            });

            let subHeader: JSX.Element | null = null;

            if (group.type === 'allergens') {
                subHeader = (
                    <div className='ms-product-specification__allergen__sub-header'>
                        <span className='ms-product-specification__allergen__sub-header__yes'>{this._attributesLocale.values.contains}</span>
                        <span className='ms-product-specification__allergen__sub-header__can-contain'>{this._attributesLocale.values.canContainTraces}</span>
                    </div>
                );
            }

            tables.push({
                name: group.name,
                heading:
                    <Msdyn365.Text
                        className='ms-product-specification__heading'
                        tag={this.props.config.heading!.tag ?? 'h2'}
                        text={group.name}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />,
                subHeader,
                tableData: table,
                modelVersion: group.modelVersion
            });
        });

        return tables;
    }

    private _createDrawerBody(content: IDlvryProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };
        return content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });
    }

    private _renderAddionalMedia(): JSX.Element | null {
        const {
            data: { searchMediaLocation }
        } = this.props;

        const mediaLocationsToDisplay = searchMediaLocation.result?.filter((mediaLocation) => mediaLocation.IsAdditionalMedia);

        if (!mediaLocationsToDisplay || mediaLocationsToDisplay.length <= 0) {
            return null;
        }

        return (
            <div className='ms-product-specification__additional-media'>
                <ul>
                    {mediaLocationsToDisplay
                        .sort(sortMediaLocations)
                        .map((mediaLocation) => {
                            return (
                                <li key={mediaLocation.Uri}>
                                    <a href={mediaLocation.Uri} target='_blank' rel='noreferrer noopener'>
                                        {mediaLocation.AltText}
                                    </a>
                                </li>
                            );
                        }
                    )}
                </ul>
            </div>
        );
    }

    private _renderCuratedInformation(): JSX.Element | null {
        return null;
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            // eslint-disable-next-line react/no-danger
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default ProductSpecification;
